import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { languageCodeSchema, languageInstruction } from "@/lib/ai-language";

const flashcardsTool = {
  type: "function",
  function: {
    name: "generate_flashcards",
    description: "Generate spaced-repetition flashcards for a concept",
    parameters: {
      type: "object",
      properties: {
        cards: {
          type: "array",
          items: {
            type: "object",
            properties: {
              front: { type: "string" },
              back: { type: "string" },
            },
            required: ["front", "back"],
          },
        },
      },
      required: ["cards"],
    },
  },
};

const quizTool = {
  type: "function",
  function: {
    name: "generate_quiz",
    description: "Generate a multiple-choice quiz on a concept",
    parameters: {
      type: "object",
      properties: {
        title: { type: "string" },
        questions: {
          type: "array",
          items: {
            type: "object",
            properties: {
              question: { type: "string" },
              choices: { type: "array", items: { type: "string" }, minItems: 4, maxItems: 4 },
              correctIndex: { type: "integer", minimum: 0, maximum: 3 },
              explanation: { type: "string" },
            },
            required: ["question", "choices", "correctIndex", "explanation"],
          },
        },
      },
      required: ["title", "questions"],
    },
  },
};

const plannerTool = {
  type: "function",
  function: {
    name: "generate_plan",
    description: "Generate a day-by-day study plan",
    parameters: {
      type: "object",
      properties: {
        tasks: {
          type: "array",
          items: {
            type: "object",
            properties: {
              day_offset: { type: "integer" },
              task: { type: "string" },
            },
            required: ["day_offset", "task"],
          },
        },
      },
      required: ["tasks"],
    },
  },
};

async function callAI(system: string, user: string, tool: unknown, toolName: string) {
  const apiKey = process.env.LOVABLE_API_KEY;
  if (!apiKey) throw new Error("AI not configured");
  const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({
      model: "google/gemini-2.5-flash",
      messages: [
        { role: "system", content: system },
        { role: "user", content: user },
      ],
      tools: [tool],
      tool_choice: { type: "function", function: { name: toolName } },
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`AI gateway ${res.status}: ${text.slice(0, 200)}`);
  }
  const data = await res.json();
  const call = data?.choices?.[0]?.message?.tool_calls?.[0];
  if (!call?.function?.arguments) throw new Error("AI returned no tool call");
  return JSON.parse(call.function.arguments);
}

async function gate(context: { supabase: import("@supabase/supabase-js").SupabaseClient; userId: string }, action: string) {
  const { data: rl, error: rateError } = await context.supabase.rpc("check_rate_limit", {
    p_action: `study_${action}`,
    p_max_requests: 10,
    p_window_seconds: 60,
  });
  if (rateError || !rl || (rl as { allowed: boolean }).allowed === false) {
    throw new Error("Too many requests. Please wait a moment.");
  }
  const { data: isPaid, error: entitlementError } = await context.supabase.rpc("has_premium_access", {
    _user_id: context.userId,
  });
  if (entitlementError || !isPaid) throw new Error("AI study tools require a Pro subscription.");
}

export const genFlashcardsFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ concept: z.string().min(1).max(200), count: z.number().int().min(3).max(10).default(5), language: languageCodeSchema }).parse(d))
  .handler(async ({ data, context }) => {
    await gate(context, "flashcards");
    const out = await callAI(
      `You are a study coach. Create high-quality flashcards for spaced repetition. Fronts are short prompts; backs are concise, accurate answers.${languageInstruction(data.language)}`,
      `Concept: ${data.concept}\nGenerate exactly ${data.count} flashcards.`,
      flashcardsTool,
      "generate_flashcards",
    );
    return { cards: (out.cards ?? []) as Array<{ front: string; back: string }> };
  });

export const genQuizFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ concept: z.string().min(1).max(200), count: z.number().int().min(3).max(10).default(5), language: languageCodeSchema }).parse(d))
  .handler(async ({ data, context }) => {
    await gate(context, "quiz");
    const out = await callAI(
      `You create fair 4-option multiple-choice quizzes with one clearly correct answer and plausible distractors. Include a brief explanation for the correct answer.${languageInstruction(data.language)}`,
      `Concept: ${data.concept}\nGenerate a quiz with exactly ${data.count} questions. Return correctIndex as 0-3.`,
      quizTool,
      "generate_quiz",
    );
    return { title: out.title as string, questions: (out.questions ?? []) as Array<{ question: string; choices: string[]; correctIndex: number; explanation: string }> };
  });

export const genPlanFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      title: z.string().min(1).max(200),
      exam_date: z.string().nullable(),
      concepts: z.array(z.string().max(100)).max(20),
      language: languageCodeSchema,
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await gate(context, "plan");
    const days = data.exam_date
      ? Math.max(1, Math.min(60, Math.ceil((new Date(data.exam_date).getTime() - Date.now()) / 86400000)))
      : 7;
    const out = await callAI(
      `You are a study planner. Produce a realistic day-by-day plan with 1-2 focused tasks per day. Cover the given concepts, escalate difficulty, and leave the last day for review.${languageInstruction(data.language)}`,
      `Goal: ${data.title}\nConcepts: ${data.concepts.join(", ") || "general"}\nDays until exam: ${days}. day_offset ranges 0..${days - 1}.`,
      plannerTool,
      "generate_plan",
    );
    return { tasks: (out.tasks ?? []) as Array<{ day_offset: number; task: string }> };
  });